/**
 * ProfileSetup — post-payment profile completion flow (2 steps).
 *
 * Step 1: Name, country, city, coaching role, experience, age groups
 * Step 2: Profile pic, bio, organization, socials, AI coaching summary
 *
 * Saves directly to Supabase profiles table.
 */

import { toJpeg } from "../../utils/imageResize";
import React, { useState, useRef, useEffect } from "react";
import { ChevronRight, ChevronDown, Search, Camera, Instagram, Globe, Sparkles, Loader2, LogOut } from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { bamConfirm } from "../shared/dialog";
import { logEvent } from "../utils/analytics";
import { COUNTRY_FLAGS } from "./onboardingConstants";
import { TrainingBadge } from "./TrainingBadge";

const COACHING_ROLES = [
  "Skills Trainer", "Team Coach", "Performance Coach", "Parent/Player",
];

const EXPERIENCE_LEVELS = [
  "Less than 1 year", "1-3 years", "3-5 years",
  "5-10 years", "10+ years",
];

const AGE_GROUPS = [
  "U8", "U9\u2013U14", "U14\u2013U18", "College", "Pro",
];

const COUNTRIES = Object.keys(COUNTRY_FLAGS).sort();

/* ── Country Dropdown ── */
function CountrySelect({ value, onChange, inp }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = search
    ? COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()))
    : COUNTRIES;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onClick={() => { setOpen(!open); setSearch(""); }}
        style={{ ...inp, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 44 }}>
        <span style={{ color: value ? "#fff" : "#666" }}>
          {value ? `${COUNTRY_FLAGS[value] || ""} ${value}` : "Select country"}
        </span>
        <ChevronDown size={14} color="#666" style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "rotate(0)" }} />
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
          marginTop: 4, maxHeight: 220, overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 8 }}>
            <Search size={13} color="#666" />
            <input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: 13, fontFamily: "inherit" }} />
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 && <div style={{ padding: "12px 14px", fontSize: 12, color: "#666" }}>No matches</div>}
            {filtered.map((c) => (
              <div key={c} onClick={() => { onChange(c); setOpen(false); setSearch(""); }}
                style={{ padding: "9px 14px", fontSize: 13, color: value === c ? GOLD : "#ccc", cursor: "pointer", background: value === c ? `${GOLD}10` : "transparent", display: "flex", alignItems: "center", gap: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD}10`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = value === c ? `${GOLD}10` : "transparent"; }}>
                <span style={{ fontSize: 16 }}>{COUNTRY_FLAGS[c]}</span>{c}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ProfileSetup({ sbUser, onComplete }) {
  const [step, setStep] = useState(1);

  // Step 1
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [roles, setRoles] = useState([]);
  const [experience, setExperience] = useState("");
  const [ageGroups, setAgeGroups] = useState([]);

  // Step 2
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [bio, setBio] = useState("");
  const [org, setOrg] = useState("");
  const [ig, setIg] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [website, setWebsite] = useState("");
  const [workingOn, setWorkingOn] = useState("");
  const [strengths, setStrengths] = useState("");
  const [aiSummary, setAiSummary] = useState(null); // { workingOn: [...], strengths: [...] }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const photoRef = useRef(null);

  // Prefill from the existing profile row so a coach who's only missing
  // ONE field (e.g. the launch-day audit found 3 paid users with first
  // name set but no role/location) doesn't have to retype everything
  // they already filled in. Without this prefill the forced re-onboarding
  // gate punishes them for past completeness.
  //
  // Best-effort — RLS allows self-read on profiles, so this fetch returns
  // either the user's own row or {data: null} on transient error. Any
  // error here is swallowed; the form just opens blank, which is the
  // pre-prefill behavior.
  useEffect(() => {
    if (!sbUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, country, city, role, experience, age_groups, bio, org, ig, x, website, avatar_url")
        .eq("id", sbUser.id)
        .maybeSingle();
      if (cancelled || !data) return;
      if (data.first_name) setFirstName(data.first_name);
      if (data.last_name)  setLastName(data.last_name);
      if (data.country)    setCountry(data.country);
      if (data.city)       setCity(data.city);
      // Roles are saved as " / "-joined string; reverse on load.
      if (data.role) {
        const parsed = data.role.split(" / ").map((r) => r.trim()).filter(Boolean);
        if (parsed.length > 0) setRoles(parsed);
      }
      if (data.experience)               setExperience(data.experience);
      if (Array.isArray(data.age_groups)) setAgeGroups(data.age_groups);
      if (data.bio)        setBio(data.bio);
      if (data.org)        setOrg(data.org);
      if (data.ig)         setIg(data.ig);
      if (data.x)          setXHandle(data.x);
      if (data.website)    setWebsite(data.website);
      // avatar_url shows the existing photo as the preview so the user
      // doesn't have to re-upload. Photo file state stays null — only
      // touched if they pick a new file.
      if (data.avatar_url) setPhotoPreview(data.avatar_url);
    })();
    return () => { cancelled = true; };
  }, [sbUser?.id]);

  const toggleRole = (r) => setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
  const toggleAge = (a) => setAgeGroups((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);

  // Step 1 gate. City + country are required so coaches actually appear
  // on the community map (otherwise we'd geocode null + null and they'd
  // never show up). The previous version made these optional and the
  // map had a 47% miss rate at the EUFP launch — most coaches just
  // skipped the field.
  // A full name, too (Coleman, 2026-10-13: "no one should just be called
  // coach they have to fill in name"). Without one, every place that shows
  // a coach to other coaches fell back to "Coach".
  // Experience and the age groups they coach, too (Coleman, 2026-11-02: "might as well make it required"): they're
  // what marketing and the platform's suggestions are built on, and they were blank for too many coaches.
  const canStep1 = firstName.trim() && lastName.trim() && roles.length > 0 && city.trim() && country.trim() && !!experience && ageGroups.length > 0;

  // Surface exactly what's missing so coaches don't get stuck staring at
  // a disabled button (which is what the support thread was about — Rithvik
  // filled in city + last name but missed picking a role / country and
  // had no way to know).
  const missingFields = (() => {
    const m = [];
    if (!firstName.trim())  m.push("first name");
    if (!lastName.trim())   m.push("last name");
    if (!country.trim())    m.push("country");
    if (!city.trim())       m.push("city");
    if (roles.length === 0) m.push("a coaching role");
    if (!experience)        m.push("your experience");
    if (ageGroups.length === 0) m.push("the age groups you coach");
    return m;
  })();
  const missingHint = missingFields.length
    ? `Add ${missingFields.join(", ")} to continue`
    : null;

  // Safety valve — anyone truly stuck (browser quirk, weird auth state,
  // wrong account) needs an escape. Sign out + force a reload.
  const escapeHatch = async () => {
    if (!(await bamConfirm({ title: "Sign out and start over?", confirmLabel: "Sign out", danger: true }))) return;
    try { await supabase.auth.signOut(); } catch {}
    try {
      // Clear localStorage flags that gate the onboarding modal
      localStorage.removeItem("onboardingComplete");
      localStorage.removeItem("bam_user_name");
    } catch {}
    window.location.href = "/";
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const generateAiSummary = async () => {
    if (!workingOn.trim() && !strengths.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch("/api/ask-bam", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: [{
            role: "user",
            content: `I'm a basketball coach setting up my profile. Summarize the following into clean, concise bullet points (1-3 each) that I can share on my coaching profile. Keep it professional and specific to coaching.

${workingOn.trim() ? `WHAT I'M WORKING ON / LOOKING FOR:\n${workingOn}\n` : ""}
${strengths.trim() ? `MY STRENGTHS AS A COACH:\n${strengths}` : ""}

Respond in this exact JSON format:
{"workingOn": ["bullet 1", "bullet 2"], "strengths": ["bullet 1", "bullet 2"]}

Only include the arrays that have input. Keep each bullet under 15 words.`
          }],
        }),
      });
      if (!res.ok) {
        // Don't block profile setup on AI failure — surface it inline and
        // let the coach retry or skip. (The early return here used to also
        // skip setAiLoading(false), stranding the button on "Summarizing…".)
        console.warn("[AI Summary] request failed:", res.status);
 setAiError("Couldn't generate a summary: you can try again or just continue.");
        return;
      }
      const data = await res.json();
      // Parse AI response — try to extract JSON
      const text = data.reply || data.content || "";
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        setAiSummary(JSON.parse(jsonMatch[0]));
      } else {
 setAiError("Couldn't generate a summary: you can try again or just continue.");
      }
    } catch (e) {
      console.error("[AI Summary]", e);
 setAiError("Couldn't generate a summary: you can try again or just continue.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Upload photo if provided
      let avatarUrl = null;
      if (photo) {
        // Shrunk to 1024px JPEG first, as the profile panel does. Uploading
        // the phone's original is how multi-megabyte avatars got in.
        let file = photo;
        try { file = await toJpeg(photo, 1024); } catch { /* keep the original */ }
        const ext = file === photo ? photo.name.split(".").pop() : "jpg";
        const path = `avatars/${sbUser.id}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("avatars")
          .upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
          avatarUrl = urlData?.publicUrl;
        }
      }

      // Build bio with AI summaries if available
      let fullBio = bio.trim();
      if (aiSummary) {
        const parts = [];
        if (aiSummary.workingOn?.length) parts.push("Working on: " + aiSummary.workingOn.join(" \u00B7 "));
        if (aiSummary.strengths?.length) parts.push("Strengths: " + aiSummary.strengths.join(" \u00B7 "));
        if (parts.length && !fullBio) fullBio = parts.join("\n");
        else if (parts.length) fullBio += "\n\n" + parts.join("\n");
      }

      const updates = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        country: country.trim() || null,
        city: city.trim() || null,
        role: roles.join(" / ") || null,
        experience: experience || null,
        age_groups: ageGroups.length > 0 ? ageGroups : null,
        bio: fullBio || null,
        org: org.trim() || null,
        ig: ig.trim() || null,
        x: xHandle.trim() || null,
        website: website.trim() || null,
        // The flag that says setup is done (it was never set before 2026-11-02).
        onboarding_complete: true,
      };
      if (avatarUrl) updates.avatar_url = avatarUrl;

      // Geocode city + country to lat/lng so the coach appears on the
      // community map. Nominatim's free tier has a strict ~1 req/sec
      // limit per IP and silently 429s on bursts (which is exactly
      // what happens during invite waves like the EUFP launch — many
      // signups in the same hour from the same datacenter IP). Mitigation:
      //
      //   1. Send Accept-Language so we get clean English place names
      //      back (no Cyrillic / CJK results we can't parse cleanly)
      //   2. Retry once on 429 after a 1.5s backoff — covers the common
      //      "rate-limited because the previous coach geocoded just now"
      //      case
      //   3. Always still proceed to save the profile even if geocoding
      //      fails — the daily backfill script
      //      (scripts/backfill-geocodes.mjs) catches stragglers.
      if (city.trim() || country.trim()) {
        const q = encodeURIComponent([city.trim(), country.trim()].filter(Boolean).join(", "));
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`;
        const tryGeocode = async () => {
          const res = await fetch(url, { headers: { "Accept-Language": "en" } });
          if (res.status === 429) throw new Error("rate-limited");
          if (!res.ok) throw new Error(`geocode ${res.status}`);
          return res.json();
        };
        try {
          let results;
          try {
            results = await tryGeocode();
          } catch (e) {
            if (e?.message === "rate-limited") {
              await new Promise(r => setTimeout(r, 1500));
              results = await tryGeocode();
            } else {
              throw e;
            }
          }
          if (Array.isArray(results) && results.length > 0) {
            updates.latitude = parseFloat(results[0].lat);
            updates.longitude = parseFloat(results[0].lon);
          }
        } catch {
          // Swallow — backfill script catches us.
        }
      }

      // Use .select().maybeSingle() so we can detect the silent
      // "0 rows affected" case — the original implementation would
      // happily return success even if RLS blocked the write or the
      // row didn't exist yet, leaving users stuck on the onboarding
      // modal forever.
      const { data: updated, error: dbError } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", sbUser.id)
        .select("id")
        .maybeSingle();
      if (dbError) throw dbError;
      if (!updated) {
        throw new Error(
          "We couldn't save your profile - your session may have expired. Refresh and sign in again, then retry."
        );
      }

      logEvent("profile_setup_completed", { roles: roles.join(","), experience, hasPhoto: !!photo, hasBio: !!bio });
      try { localStorage.setItem("bam_user_name", firstName.trim()); } catch {}
      // Hard flag so we never re-trap the user in onboarding on cold
      // app start, even if a subsequent profile fetch silently returns
      // 0 rows (iOS WKWebView edge case).
      try { localStorage.setItem("bam_onboard_done", "1"); } catch {}
      onComplete?.({
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        role: roles.join(" / "),
        location: [city.trim(), country.trim()].filter(Boolean).join(", "),
      });
    } catch (e) {
      console.error("[ProfileSetup] save failed:", e);
      setError(e.message || "Couldn't save \u2014 try again");
      setSaving(false);
    }
  };

  const chipStyle = (active) => ({
    padding: "8px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
    border: `1px solid ${active ? GOLD : "rgba(255,255,255,0.1)"}`,
    background: active ? `${GOLD}20` : "transparent",
    color: active ? GOLD : "#aaa",
    transition: "all .15s ease",
  });

  const inp = {
    width: "100%", background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10,
    padding: "12px 14px", fontSize: 14, color: "#fff", outline: "none",
    fontFamily: "inherit", boxSizing: "border-box",
  };

  const labelStyle = { fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, display: "block" };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9500, background: "#050505",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, overflow: "auto", fontFamily: "'DM Sans', sans-serif",
    }}>
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse 800px 500px at 50% 20%, rgba(226,221,159,0.06), transparent 70%)",
      }} />

      <div style={{
        position: "relative", width: "100%", maxWidth: 500,
        background: "linear-gradient(180deg, #131313, #0E0E0E)",
        border: `1px solid rgba(226,221,159,0.15)`, borderRadius: 20,
        boxShadow: "0 40px 120px rgba(0,0,0,0.6)",
        maxHeight: "90vh",
        // Flex column layout so we can pin the action footer to the
        // bottom regardless of how tall the form gets \u2014 the previous
        // single-overflow-block design hid the Next button below the
        // fold and stranded users like Rithvik who'd filled the form
        // but couldn't find the button.
        display: "flex", flexDirection: "column",
      }}>
        {/* Sign-out escape hatch \u2014 top-right of the modal so anyone
            stuck (wrong account, stale session, browser quirk) has a
            visible way out. */}
        <button onClick={escapeHatch}
          aria-label="Sign out"
          title="Sign out and start over"
          style={{
            position: "absolute", top: 12, right: 12, zIndex: 6,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8,
            padding: "5px 9px",
            color: "#888",
            fontSize: 10.5, fontWeight: 700, letterSpacing: .4,
            cursor: "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>
          <LogOut size={11}/> Sign out
        </button>

        {/* Scrollable body \u2014 flex:1, overflow:auto. WebKit overflow
            scrolling helps iOS Safari render fluid momentum scroll
            inside the centered modal. */}
        <div style={{
          flex: 1, overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          padding: "32px 28px 16px",
        }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <img src={BAM_LOGO_PNG} alt="BAM" style={{ width: 36, height: 36, objectFit: "contain", margin: "0 auto 14px", display: "block", filter: "drop-shadow(0 0 16px rgba(226,221,159,0.25))" }} />
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 2, color: "#fff" }}>
              {step === 1 ? "WELCOME IN, COACH" : "MAKE IT YOURS"}
            </div>
            <p style={{ fontSize: 13, color: "#999", marginTop: 6, lineHeight: 1.5 }}>
              {step === 1
                ? "Quick setup \u2014 takes 30 seconds. This helps other coaches find you in the community."
                : "Add a photo and tell the community about yourself. You can always update this later."}
            </p>
            {/* Step dots */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }}>
              <div style={{ width: 24, height: 3, borderRadius: 2, background: GOLD }} />
              <div style={{ width: 24, height: 3, borderRadius: 2, background: step === 2 ? GOLD : "rgba(255,255,255,0.1)" }} />
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {step === 1 ? (
            <>
              {/* Name */}
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First name *</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" style={inp} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last name *</label>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" style={inp} />
                </div>
              </div>

              {/* Location */}
              <div style={{ display: "flex", gap: 10 }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <label style={labelStyle}>Country *</label>
                  <CountrySelect value={country} onChange={setCountry} inp={inp} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>City *</label>
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Miami, Brooklyn, Lisbon" style={inp} required/>
                </div>
              </div>
              <div style={{fontSize:11,color:"#9A9488",marginTop:-4,marginBottom:4,lineHeight:1.5}}>
 We use this to put you on the global coach community map. Pick a real city: typing a country name puts you in the middle of nowhere.
              </div>

              {/* Coaching roles */}
              <div>
                <label style={labelStyle}>What do you coach? *</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {COACHING_ROLES.map((r) => (
                    <div key={r} onClick={() => toggleRole(r)} style={chipStyle(roles.includes(r))}>{r}</div>
                  ))}
                </div>
              </div>

              {/* Experience */}
              <div>
                <label style={labelStyle}>Experience level</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {EXPERIENCE_LEVELS.map((e) => (
                    <div key={e} onClick={() => setExperience(experience === e ? "" : e)} style={chipStyle(experience === e)}>{e}</div>
                  ))}
                </div>
              </div>

              {/* Age groups */}
              <div>
                <label style={labelStyle}>Age groups you work with</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {AGE_GROUPS.map((a) => (
                    <div key={a} onClick={() => toggleAge(a)} style={chipStyle(ageGroups.includes(a))}>{a}</div>
                  ))}
                </div>
              </div>

            </>
          ) : (
            <>
              {/* Profile Photo */}
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
                <div onClick={() => photoRef.current?.click()}
                  style={{
                    width: 80, height: 80, borderRadius: "50%", cursor: "pointer",
                    background: photoPreview ? `url(${photoPreview}) center/cover` : `${GOLD}15`,
                    border: `2px solid ${photoPreview ? GOLD : "rgba(255,255,255,0.1)"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    position: "relative", overflow: "hidden",
                    transition: "border-color .15s",
                  }}>
                  {!photoPreview && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <Camera size={20} color="#888" />
                      <span style={{ fontSize: 9, color: "#888", fontWeight: 600 }}>ADD PHOTO</span>
                    </div>
                  )}
                  {photoPreview && (
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.6)", padding: "3px 0", textAlign: "center" }}>
                      <span style={{ fontSize: 9, color: "#fff", fontWeight: 600 }}>CHANGE</span>
                    </div>
                  )}
                </div>
                <input ref={photoRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} />
              </div>

              {/* Organization */}
              <div>
                <label style={labelStyle}>Organization / Team</label>
                <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Your team or organization" style={inp} />
              </div>

              {/* Bio */}
              <div>
                <label style={labelStyle}>Bio</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell the community about yourself and your coaching journey..."
                  rows={3} style={{ ...inp, resize: "vertical", minHeight: 72 }} />
              </div>

              {/* Socials */}
              <div>
                <label style={labelStyle}>Socials</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Instagram size={16} color="#888" style={{ flexShrink: 0 }} />
                    <input value={ig} onChange={(e) => setIg(e.target.value)} placeholder="instagram handle" style={{ ...inp, padding: "10px 12px", fontSize: 13 }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16, color: "#888", fontWeight: 800, width: 16, textAlign: "center", flexShrink: 0 }}>𝕏</span>
                    <input value={xHandle} onChange={(e) => setXHandle(e.target.value)} placeholder="x handle" style={{ ...inp, padding: "10px 12px", fontSize: 13 }} />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Globe size={16} color="#888" style={{ flexShrink: 0 }} />
                    <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="website url" style={{ ...inp, padding: "10px 12px", fontSize: 13 }} />
                  </div>
                </div>
              </div>

              {/* AI Coaching Summary */}
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  <Sparkles size={14} color={GOLD} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: 1 }}>AI Coach Profile</span>
                  <TrainingBadge />
                  <span style={{ fontSize: 10, color: "#666", marginLeft: "auto" }}>Optional</span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label style={{ ...labelStyle, color: "#777" }}>What are you working on or looking for?</label>
                    <textarea value={workingOn} onChange={(e) => setWorkingOn(e.target.value)}
                      placeholder="e.g. Getting better at designing constraints-based drills, building a youth development curriculum..."
                      rows={2} style={{ ...inp, resize: "vertical", minHeight: 56, fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ ...labelStyle, color: "#777" }}>Your strengths as a coach</label>
                    <textarea value={strengths} onChange={(e) => setStrengths(e.target.value)}
                      placeholder="e.g. 1v1 skill development, building culture, reading the game, player relationships..."
                      rows={2} style={{ ...inp, resize: "vertical", minHeight: 56, fontSize: 13 }} />
                  </div>

                  {(workingOn.trim() || strengths.trim()) && !aiSummary && (
                    <button onClick={()=>{setAiError("");generateAiSummary();}} disabled={aiLoading}
                      style={{
                        padding: "10px 16px", borderRadius: 8, border: `1px solid ${GOLD}40`,
                        background: `${GOLD}10`, color: GOLD, fontSize: 12, fontWeight: 700,
                        cursor: aiLoading ? "wait" : "pointer", fontFamily: "inherit",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}>
                      {aiLoading ? <><Loader2 size={13} style={{ animation: "spin 1s linear infinite" }} /> Summarizing...</>
                        : <><Sparkles size={13} /> Generate Profile Summary</>}
                    </button>
                  )}

                  {aiError && !aiSummary && (
                    <div style={{ fontSize: 12, color: "#E06060", marginTop: 8, lineHeight: 1.5 }}>{aiError}</div>
                  )}
                  {aiSummary && (
                    <div style={{ padding: "12px 14px", background: `${GOLD}08`, border: `1px solid ${GOLD}20`, borderRadius: 10 }}>
                      {aiSummary.workingOn?.length > 0 && (
                        <div style={{ marginBottom: aiSummary.strengths?.length ? 8 : 0 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Working On</div>
                          {aiSummary.workingOn.map((b, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#ccc", lineHeight: 1.5, paddingLeft: 10, position: "relative" }}>
                              <span style={{ position: "absolute", left: 0, color: GOLD }}>·</span> {b}
                            </div>
                          ))}
                        </div>
                      )}
                      {aiSummary.strengths?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Strengths</div>
                          {aiSummary.strengths.map((b, i) => (
                            <div key={i} style={{ fontSize: 12, color: "#ccc", lineHeight: 1.5, paddingLeft: 10, position: "relative" }}>
                              <span style={{ position: "absolute", left: 0, color: GOLD }}>·</span> {b}
                            </div>
                          ))}
                        </div>
                      )}
                      <div onClick={() => setAiSummary(null)} style={{ fontSize: 11, color: "#888", marginTop: 8, cursor: "pointer", textAlign: "right" }}>
                        Regenerate
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div style={{ padding: "9px 12px", background: "rgba(224,96,96,0.08)", border: "1px solid rgba(224,96,96,0.25)", borderRadius: 8, fontSize: 12, color: "#ff9d9d" }}>
                  {error}
                </div>
              )}

              <div style={{ textAlign: "center", fontSize: 11, color: "#555", marginTop: 4 }}>
                You can update this anytime from your profile settings.
              </div>
            </>
          )}
          </div>
        </div>

        {/* Sticky action footer — pinned to the bottom of the modal box
            (outside the scrollable body) so the primary CTA is ALWAYS
            visible regardless of how tall the form gets or how short the
            phone viewport is. The previous design hid the Next button
            below the fold for mobile users who didn't realize the modal
            scrolled internally — see Rithvik's support ticket. */}
        <div style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          padding: "14px 28px 18px",
          background: "linear-gradient(180deg, rgba(14,14,14,0) 0%, #0E0E0E 18%, #0E0E0E 100%)",
        }}>
          {/* "What's missing" hint when the primary action is disabled.
              Phrasing is specific (lists the missing fields) instead of
              a generic "fill all required fields" so coaches don't have
              to hunt. */}
          {step === 1 && missingHint && (
            <div style={{
              fontSize: 11.5, color: "#bdb78b",
              textAlign: "center", marginBottom: 10, lineHeight: 1.5,
            }}>
              {missingHint}
            </div>
          )}

          {step === 1 ? (
            <button onClick={() => { if (canStep1) setStep(2); }}
              disabled={!canStep1}
              style={{
                width: "100%", padding: "14px",
                background: canStep1 ? `linear-gradient(180deg, ${GOLD}, #d4ce8a)` : "rgba(255,255,255,0.06)",
                color: canStep1 ? "#0A0A0A" : "#666",
                border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800,
                fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1.5, textTransform: "uppercase",
                cursor: canStep1 ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                boxShadow: canStep1 ? `0 8px 28px ${GOLD}30` : "none",
              }}>
              Next <ChevronRight size={15} />
            </button>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)}
                style={{
                  flex: 1, padding: "13px", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10,
                  background: "transparent", color: "#aaa", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                Back
              </button>
              <button onClick={handleSubmit} disabled={saving}
                style={{
                  flex: 2, padding: "13px",
                  background: `linear-gradient(180deg, ${GOLD}, #d4ce8a)`,
                  color: "#0A0A0A", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 800,
                  fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1.5, textTransform: "uppercase",
                  cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1,
                  boxShadow: `0 8px 32px ${GOLD}30`,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                {saving ? "Saving..." : "Enter the Platform"} {!saving && <ChevronRight size={15} />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
